import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { TokenManagerService } from '../services/token-manager.service';
import { AuthErrorService } from '../services/auth-error.service';
import { Organization, UserService } from '../services/user.service';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../../environments/environment';

const REQUIRED_SCOPES_ASSETWORKS = ['LogIn'];
const ENVIRONMENT_SCOPES = [
  'Environment.DEV.Scope',
  'Environment.TST.Scope',
  'Environment.UAT.Scope',
  'Environment.PRD.Scope',
];

export const ScopesGuard: CanActivateFn = async () => {
  const tokenManagerService = inject(TokenManagerService);
  const userService = inject(UserService);
  const authErrorService = inject(AuthErrorService);
  const msalService = inject(MsalService);
  const router = inject(Router);

  function denyAndRedirectToLogin(): UrlTree {
    authErrorService.setErrorMessage(
      'You are not authorized to access this application'
    );

    const organizationId: string =
      userService.getUserInfo()?.organizationId || '';

    tokenManagerService.clearAccessToken();
    userService.clearOrganization();

    return router.createUrlTree(['/logout'], {
      queryParams: {
        reason: 'Scope Mismatch',
        organizationId: organizationId,
      },
    });
  }

  function validateScopes(): boolean {
    const organization: Organization | null = userService.getOrganization();
    let hasRequiredScopes: boolean = true;

    // Assetworks employees need these scopes
    if (organization === 'assetworks-employee') {
      hasRequiredScopes = tokenManagerService.containsAllScopes(
        REQUIRED_SCOPES_ASSETWORKS
      );
    }

    const hasEnvironmentScopes: boolean =
      tokenManagerService.containsAnyScopes(ENVIRONMENT_SCOPES);

    return hasRequiredScopes && hasEnvironmentScopes;
  }

  async function tryAcquireMsalToken(): Promise<boolean> {
    const accounts = msalService.instance.getAllAccounts();
    if (!accounts.length) return false;

    try {
      const result = await msalService.instance.acquireTokenSilent({
        scopes: environment.apiConfig.scopes,
        account: msalService.instance.getActiveAccount() ?? accounts[0],
      });
      tokenManagerService.setAccessToken(result.accessToken);
      return true;
    } catch {
      return false;
    }
  }

  // Check if we already have token cached, check validity of token if so
  const currentToken = tokenManagerService.getAccessToken();

  if (currentToken && tokenManagerService.getScopes().size > 0) {
    return validateScopes() || denyAndRedirectToLogin();
  }

  // On initial msal login, the token isn't currently cached within the tokenManagerService,
  // We use Msal to directly check for its existence and run the same validity check above
  const hasMsalToken = await tryAcquireMsalToken();
  if (hasMsalToken) {
    return validateScopes() || denyAndRedirectToLogin();
  }

  return denyAndRedirectToLogin();
};
